import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";

import { useAppSelector } from "app/hooks";
import { FAX_UNASSIGNED_QUEUE_FEATURE } from "constants/feature-flags";
import { selectLDIdentified } from "features/auth/auth-slice";

/**
 * How long the Fax Center waits for LaunchDarkly before treating the flag as off. Bounded so a
 * blocked LaunchDarkly cannot leave the page spinning; the real wait is well under a second.
 */
const FLAG_DELIVERY_GRACE_MS = 3_000;

/**
 * Whether the Fax Center's Unassigned Faxes queue is turned on (ENG-2511).
 *
 * The one read of the flag: the list query (the `read` field and the `unassigned` params), the
 * tabs, the rows, the page's read-all-on-open and the Assign dialog must all get the same answer,
 * or the page asks for fields it does not show, or shows markers it never fetched.
 */
export default function useFaxUnassignedQueueEnabled(): boolean {
  const flags = useFlags();

  return Boolean(flags[FAX_UNASSIGNED_QUEUE_FEATURE]);
}

/**
 * Whether LaunchDarkly has answered for the signed-in user, so the flag above means something.
 *
 * Until `identify()` lands the SDK evaluates the anonymous bootstrap context (src/index.tsx), where
 * a practice-targeted flag reads off. The Fax Center must not render on that answer: flag off, it
 * marks every fax in the clinic read on open, which would wipe the queue's unread state on a cold
 * load. `ldIdentified` alone is not enough (see use-sales-coach-route-access.hook.ts): it can be
 * set with no flag payload yet, so both are required, with a bounded wait.
 */
export function useFaxUnassignedQueueResolved(): boolean {
  const identified = useAppSelector(selectLDIdentified);
  const flags = useFlags();
  const isAnswered = Boolean(identified) && Object.keys(flags).length > 0;
  const [graceExpired, setGraceExpired] = useState(false);

  useEffect(() => {
    if (isAnswered) return undefined;
    const timer = setTimeout(
      () => setGraceExpired(true),
      FLAG_DELIVERY_GRACE_MS
    );
    return () => clearTimeout(timer);
  }, [isAnswered]);

  return isAnswered || graceExpired;
}
